import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Activity, Wallet, Target, Shield, ChevronRight, ArrowRight,
  CheckCircle2, AlertTriangle, Hourglass, BarChart3, FolderOpen,
  Calendar, Building2, FileText, DollarSign, Edit2, Trash2, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiService } from '@/lib/api';
import StartProjectNudges from './StartProjectNudges';
import MaterialOrderNudges from './MaterialOrderNudges';
import BudgetPulseNudges from './BudgetPulseNudges';
import PendingReviewsNudge from './PendingReviewsNudge';

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);

const compactDollars = (val) => {
  if (val == null) return '$0';
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return '$' + (val / 1_000_000).toFixed(1) + 'M';
  if (abs >= 1_000) return '$' + (val / 1_000).toFixed(0) + 'K';
  return '$' + Math.round(val).toLocaleString();
};

// Daily-interest values are often small (e.g. $11.11/day per project).
// Show cents up to $1k, then K-compact for large portfolios.
const formatDailyInterest = (val) => {
  if (val == null) return '$0.00';
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return '$' + (val / 1_000_000).toFixed(1) + 'M';
  if (abs >= 10_000) return '$' + (val / 1_000).toFixed(0) + 'K';
  if (abs >= 1_000) return '$' + (val / 1_000).toFixed(2) + 'K';
  return '$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ---------- Compliance pill helpers ----------
const STATUS_STYLES = {
  approved: 'bg-emerald-100 text-emerald-700',
  submitted: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  expiring: 'bg-orange-100 text-orange-700',
  expired: 'bg-red-100 text-red-700',
  awaiting: 'bg-red-100 text-red-700',
  none: 'bg-slate-100 text-slate-500',
};
const PILL_LABEL = { approved: '✓', submitted: 'Sub', pending: 'Pend', expiring: 'Exp', expired: 'Expired', awaiting: '!', none: '—' };

const Pill = ({ label, status }) => (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[status] || STATUS_STYLES.none}`}>
    {label} {PILL_LABEL[status] || ''}
  </span>
);

const computeBankReadyScore = (project) => {
  // Lightweight client-side score 0-100 based on observable project attributes
  if (!project) return 0;
  let score = 50;
  if (project.loan_principal_amount && project.loan_principal_amount > 0) score += 10;
  if (project.loan_interest_rate) score += 5;
  if (project.loan_period_months) score += 5;
  if (project.legal_description) score += 5;
  if (project.return_address) score += 5;
  if (project.bank_name) score += 5;
  if (project.cover_image) score += 5;
  if (project.start_date) score += 5;
  if (project.projected_completion_date) score += 5;
  return Math.min(100, score);
};

const computeBurnPerDay = (project) => {
  // Daily Burn = current daily interest accrual on the drawn loan.
  // The backend computes this in `/api/projects` using the same formula as the
  // Loan Details page: amount_drawn * (current_rate/100) / 360.
  // We trust the backend value when present; otherwise mirror the formula
  // client-side so older cached responses still render a value.
  if (project?.daily_interest_current != null) {
    return Number(project.daily_interest_current) || 0;
  }
  const drawn = Number(project?.amount_drawn) || 0;
  const rate = Number(project?.loan_interest_rate) || 0;
  if (drawn <= 0 || rate <= 0) return 0;
  return (drawn * (rate / 100)) / 360;
};

// ---------- KPI Card ----------
const KpiCard = ({ icon: Icon, label, value, sub, color = 'blue', infoTooltip }) => {
  const palette = {
    emerald: { bg: 'from-emerald-50 to-emerald-100', border: 'border-emerald-200', icon: 'text-emerald-700', pill: 'bg-emerald-200 text-emerald-700' },
    blue: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', icon: 'text-blue-700', pill: 'bg-blue-200 text-blue-700' },
    purple: { bg: 'from-purple-50 to-purple-100', border: 'border-purple-200', icon: 'text-purple-700', pill: 'bg-purple-200 text-purple-700' },
    amber: { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', icon: 'text-amber-700', pill: 'bg-amber-200 text-amber-700' },
  }[color];
  return (
    <div className={`bg-gradient-to-br ${palette.bg} rounded-2xl p-4 border ${palette.border}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1.5">
        <Icon className={`w-5 h-5 ${palette.icon}`} />
        <span className={`text-[10px] font-semibold ${palette.pill} px-2 py-0.5 rounded-full inline-flex items-center gap-1`}>
          {label}
          {infoTooltip && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center hover:opacity-70 focus:outline-none"
                    aria-label={`What is ${label}?`}
                    data-testid={`kpi-info-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-snug">
                  {infoTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      </div>
      <p className="text-2xl font-bold text-slate-900 font-mono">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
};

// ---------- Loans table row ----------
const LoanRow = ({ project, onClick, onEdit, onDelete, isTeamMember }) => {
  const burn = computeBurnPerDay(project);
  const score = computeBankReadyScore(project);
  const drawn = project.amount_drawn || 0;
  const principal = project.loan_principal_amount || project.total_budget || 0;

  // Loan term display
  let termLabel = '—';
  if (project.loan_term_extended && project.loan_extended_to_months) {
    termLabel = `${project.loan_extended_to_months}mo *`;
  } else if (project.loan_period_months) {
    termLabel = `${project.loan_period_months}mo`;
  }

  // Listing label
  let listingLabel = null;
  if (project.listing_type === 'brokerage' && project.brokerage_name) {
    listingLabel = project.brokerage_name;
  } else if (project.listing_type === 'owner_listed') {
    listingLabel = 'Owner-listed';
  } else if (project.listing_type === 'rental') {
    listingLabel = 'Rental';
  } else if (project.listing_type === 'n_a') {
    listingLabel = 'N/A';
  }

  const scoreColor = score >= 85 ? 'text-emerald-600' : score >= 70 ? 'text-blue-600' : 'text-amber-600';

  return (
    <div
      onClick={onClick}
      className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-xs border-b border-slate-100 last:border-b-0 group"
      data-testid={`command-center-loan-row-${project.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 truncate">{project.name}</p>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <span className="truncate">{project.address}</span>
          {listingLabel && (
            <>
              <span className="flex-shrink-0">·</span>
              <span className="truncate text-slate-600">{listingLabel}</span>
            </>
          )}
        </div>
      </div>
      <div className="text-right w-20 flex-shrink-0">
        <p className="font-mono text-slate-700">{compactDollars(principal)}</p>
        <p className="text-[9px] text-slate-400">Loan</p>
      </div>
      <div className="text-center w-14 flex-shrink-0">
        <p className="font-mono text-slate-700">{termLabel}</p>
        <p className="text-[9px] text-slate-400">Term{project.loan_term_extended ? ' (ext)' : ''}</p>
      </div>
      <div className="text-right w-16 flex-shrink-0">
        <p className="font-mono text-blue-600">${burn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/d</p>
        <p className="text-[9px] text-slate-400">Interest</p>
      </div>
      <div className="text-right w-12 flex-shrink-0">
        <p className={`font-bold ${scoreColor}`}>{score}</p>
        <p className="text-[9px] text-slate-400">Score</p>
      </div>
      {!isTeamMember && (onEdit || onDelete) && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {onEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(project, e); }}
              className="w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center"
              title="Edit project"
              data-testid={`command-center-edit-${project.id}`}
            >
              <Edit2 className="w-3 h-3 text-slate-500" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(project.id, e); }}
              className="w-6 h-6 rounded-full hover:bg-red-50 flex items-center justify-center"
              title="Delete project"
              data-testid={`command-center-delete-${project.id}`}
            >
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          )}
        </div>
      )}
      <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
    </div>
  );
};

// ---------- Compliance section ----------
const ComplianceSection = ({ contractorRows, onViewAll }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl flex flex-col" style={{ maxHeight: 480 }}>
      <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-amber-600" />Contractor Compliance
          </h3>
          <p className="text-[10px] text-slate-500">W-9 · COI · Lien Waiver</p>
        </div>
        <Badge variant="secondary" className="text-[10px]">{contractorRows.length}</Badge>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5" data-testid="compliance-list">
        {contractorRows.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-slate-500">
            <Shield className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No contractors yet
          </div>
        ) : (
          contractorRows.map((c, i) => (
            <div
              key={c.id || i}
              className="p-2 bg-slate-50 hover:bg-slate-100 rounded-lg cursor-pointer transition-colors"
              onClick={() => c.onClick && c.onClick()}
              data-testid={`compliance-row-${i}`}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-xs truncate">{c.name}</div>
                  {c.contactName && (
                    <div className="text-[10px] text-slate-500 truncate">{c.contactName}</div>
                  )}
                </div>
                {c.trade && (
                  <span className="text-[9px] text-slate-500 ml-1.5 flex-shrink-0">{c.trade}</span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Pill label="W9" status={c.w9} />
                <Pill label="COI" status={c.coi} />
                <Pill label="Lien" status={c.lien} />
              </div>
            </div>
          ))
        )}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-[11px] h-7"
          onClick={onViewAll}
          data-testid="compliance-view-all-btn"
        >
          View all contractors <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ---------- Underwrite a Deal (replaces standalone Financial Analysis link) ----------
const UnderwriteCard = ({ onClick }) => (
  <div
    onClick={onClick}
    className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-4 text-white cursor-pointer hover:shadow-xl transition-shadow"
    data-testid="underwrite-deal-card"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
        <BarChart3 className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wide text-indigo-200">Underwrite a Deal</p>
        <h3 className="font-bold text-base mb-1">Financial Analysis Tools</h3>
        <p className="text-xs text-indigo-100 mb-2">
          Run cash-flow projections, ROI, IRR, and stress tests on potential deals before funding.
        </p>
        <Button size="sm" variant="secondary" className="bg-white text-indigo-700 hover:bg-indigo-50 text-xs h-7">
          Open Analysis Suite <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  </div>
);

// =================== MAIN ===================
export default function CommandCenterLayout({
  projects = [],
  totalFundsAvailable = 0,
  activeCount = 0,
  scheduledCount = 0,
  totalBudget = 0,
  onNewProject,
  onRequestDraw,
  onDeleteProject,
  onEditProject,
  canCreateProject = true,
  isTeamMember = false,
  userId = null,
}) {
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);

  // Aggregate metrics
  const portfolioScore = useMemo(() => {
    if (!projects.length) return 0;
    const scores = projects.map(computeBankReadyScore);
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [projects]);

  const totalDailyBurn = useMemo(() => {
    return projects.reduce((sum, p) => sum + computeBurnPerDay(p), 0);
  }, [projects]);

  // MTD interest accrued so far this month (estimate): sum of daily interest * day-of-month.
  // Useful as a lightweight "carrying cost so far this month" indicator.
  const monthToDateBurn = useMemo(() => {
    const dayOfMonth = new Date().getDate();
    return totalDailyBurn * dayOfMonth;
  }, [totalDailyBurn]);

  // Load contractors aggregated across user's projects (best-effort)
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // Pull contractors across ALL active projects (not just first 4) so the
        // panel reflects the full network. Dedupe by contractor id since the
        // same contractor can be linked to multiple projects.
        const targets = (projects || []).filter((p) => p.status === 'active' || p.status === 'in_progress');
        const seen = new Set();
        const all = [];
        const now = Date.now();
        const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
        for (const p of targets) {
          try {
            const resp = await apiService.getContractors(p.id);
            const list = resp?.data || [];
            list.forEach((c) => {
              if (seen.has(c.id)) return;
              seen.add(c.id);

              // Company name first, contact name as secondary line.
              const displayName = c.company || c.company_name || c.name || 'Unknown';
              const contactName = (c.company && c.name && c.name !== c.company) ? c.name : '';
              const tradeLabel = (c.trades && c.trades.length ? c.trades.join(', ') : c.trade) || c.specialty || '';

              // Map real backend fields (w9_received, coi_received, coi_expiration)
              // onto our pill statuses. Lien waivers are tracked per-phase, so we
              // mark 'n_a' here unless we have an explicit flag — better than
              // showing a misleading red "Lien!" for every contractor.
              let w9Status;
              if (c.w9_received) w9Status = 'approved';
              else if (c.w9_file_id || c.w9_file_url) w9Status = 'submitted';
              else w9Status = 'awaiting';

              let coiStatus;
              if (c.coi_received) {
                coiStatus = 'approved';
                // If COI has an expiration within 30 days, mark expiring
                if (c.coi_expiration) {
                  const exp = new Date(c.coi_expiration).getTime();
                  if (!Number.isNaN(exp)) {
                    if (exp < now) coiStatus = 'expired';
                    else if (exp - now < THIRTY_DAYS_MS) coiStatus = 'expiring';
                  }
                }
              } else if (c.coi_file_id || c.coi_file_url) {
                coiStatus = 'submitted';
              } else {
                coiStatus = 'awaiting';
              }

              const lienStatus = 'none'; // Lien waivers are per-phase; show — until aggregated server-side

              all.push({
                id: c.id,
                projectId: p.id,
                name: displayName,
                contactName,
                trade: tradeLabel,
                w9: w9Status,
                coi: coiStatus,
                lien: lienStatus,
                onClick: () => navigate(`/project/${p.id}?tab=compliance`),
              });
            });
          } catch (e) {
            // swallow per-project errors
          }
        }
        if (alive) setContractors(all);
      } catch (e) {
        // ignore
      }
    };
    if (projects.length) load();
    return () => { alive = false; };
  }, [projects, navigate]);

  const compliancePctApproved = useMemo(() => {
    if (!contractors.length) return null;
    // Approved = W-9 approved AND COI approved (lien is per-phase, excluded)
    const approvedCount = contractors.filter((c) => c.w9 === 'approved' && c.coi === 'approved').length;
    return { approved: approvedCount, total: contractors.length };
  }, [contractors]);

  const recentLoans = useMemo(() => {
    return [...projects].sort((a, b) => {
      const ad = new Date(a.created_at || 0).getTime();
      const bd = new Date(b.created_at || 0).getTime();
      return bd - ad;
    }).slice(0, 8);
  }, [projects]);

  // Notice for projects missing the new fields
  const missingFieldsCount = useMemo(() => {
    return projects.filter((p) => !p.loan_period_months || !p.listing_type).length;
  }, [projects]);

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto" data-testid="command-center-layout">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            Command Center
            <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">NEW</Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Your portfolio at a glance — KPIs, loans, and compliance in one view.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={onRequestDraw} data-testid="cc-request-draw-btn">
            <DollarSign className="w-3.5 h-3.5 mr-1" />Request Draw
          </Button>
          {!isTeamMember && canCreateProject && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-9 text-xs" onClick={onNewProject} data-testid="cc-new-project-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />New Project
            </Button>
          )}
        </div>
      </div>

      {/* Budget pulse — milestone alerts (25/50/75/90/100%, contingency, overdrawn) */}
      {!isTeamMember && userId && <BudgetPulseNudges userId={userId} />}

      {/* v2.5.7 — Pending Reviews nudge (contractor invoices/W-9s/COIs awaiting owner review) */}
      {!isTeamMember && userId && <PendingReviewsNudge userId={userId} />}

      {/* Start Project nudges — projects that need owner to press Start */}
      {!isTeamMember && userId && <StartProjectNudges userId={userId} />}

      {/* Material procurement nudges — materials nearing order-by date */}
      {!isTeamMember && userId && <MaterialOrderNudges userId={userId} />}

      {/* Feature notice for missing data */}
      {missingFieldsCount > 0 && !isTeamMember && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-2 text-xs" data-testid="cc-missing-fields-notice">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-amber-900">
            <span className="font-bold">{missingFieldsCount}</span> project{missingFieldsCount === 1 ? '' : 's'} missing loan term or listing details.
            Edit any project (pencil icon) to add them.
          </span>
        </div>
      )}

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard
          icon={Wallet}
          label="Available"
          value={compactDollars(totalFundsAvailable)}
          sub={`Across ${activeCount} active loan${activeCount === 1 ? '' : 's'}`}
          color="emerald"
        />
        <KpiCard
          icon={Activity}
          label="Daily Interest"
          value={formatDailyInterest(totalDailyBurn)}
          sub="Total carrying cost / day"
          color="blue"
          infoTooltip="Daily Interest = amount drawn × (current rate ÷ 100) ÷ 360 (banker's year). The carrying cost your loan accrues each day on already-drawn funds. Matches the Daily Interest figure on each project's Loan Details page."
        />
        <KpiCard
          icon={Target}
          label="Bank-Ready"
          value={`${portfolioScore}`}
          sub="Portfolio score / 100"
          color="purple"
        />
        <KpiCard
          icon={Shield}
          label="Compliance"
          value={
            compliancePctApproved
              ? `${compliancePctApproved.approved} / ${compliancePctApproved.total}`
              : '—'
          }
          sub={compliancePctApproved ? 'Contractors fully approved' : 'No contractors yet'}
          color="amber"
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Active Loans table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-blue-600" />Active Loans
                </h3>
                <p className="text-[10px] text-slate-500">Click any row to open project</p>
              </div>
              <Badge variant="secondary" className="text-[10px]">{recentLoans.length} project{recentLoans.length === 1 ? '' : 's'}</Badge>
            </div>
            {recentLoans.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500">
                <FolderOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No projects yet — create your first one to get started.</p>
                {!isTeamMember && canCreateProject && (
                  <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700" onClick={onNewProject}>
                    <Plus className="w-3 h-3 mr-1" />New Project
                  </Button>
                )}
              </div>
            ) : (
              <div data-testid="command-center-loans-table">
                {recentLoans.map((p) => (
                  <LoanRow
                    key={p.id}
                    project={p}
                    onClick={() => navigate(`/project/${p.id}`)}
                    onEdit={onEditProject}
                    onDelete={onDeleteProject}
                    isTeamMember={isTeamMember}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Underwrite a Deal card (opens Analysis Suite — Underwrite tab) */}
          <UnderwriteCard onClick={() => navigate('/analysis-suite?tab=underwrite')} />
        </div>

        {/* Right column: compliance + quick actions */}
        <div className="space-y-4">
          <ComplianceSection
            contractorRows={contractors}
            onViewAll={() => navigate('/project-hub?tab=contractors')}
          />

          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="cc-quick-actions">
            <h4 className="font-bold text-slate-900 text-sm mb-2.5 flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-blue-600" />Quick Actions
            </h4>
            <div className="space-y-1.5">
              <button
                onClick={onRequestDraw}
                className="w-full flex items-center gap-2 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-xs text-emerald-700 font-medium text-left"
                data-testid="cc-action-request-draw"
              >
                <DollarSign className="w-4 h-4" />
                <span>Request a Draw</span>
              </button>
              <button
                onClick={() => navigate('/reports')}
                className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-xs text-blue-700 font-medium text-left"
                data-testid="cc-action-reports"
              >
                <FileText className="w-4 h-4" />
                <span>Generate a Report</span>
              </button>
              <button
                onClick={() => navigate('/analysis-suite?tab=underwrite')}
                className="w-full flex items-center gap-2 px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded-lg text-xs text-purple-700 font-medium text-left"
                data-testid="cc-action-underwrite"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Underwrite a Deal</span>
              </button>
              <button
                onClick={() => navigate('/project-hub')}
                className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs text-slate-700 font-medium text-left"
                data-testid="cc-action-project-hub"
              >
                <FolderOpen className="w-4 h-4" />
                <span>Open Project Hub</span>
              </button>
            </div>
          </div>

          {/* Portfolio summary */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h4 className="font-bold text-slate-900 text-sm mb-2 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-slate-600" />Portfolio Summary
            </h4>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Active</span>
                <span className="font-semibold text-emerald-700">{activeCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Scheduled</span>
                <span className="font-semibold text-blue-700">{scheduledCount}</span>
              </div>
              <div className="flex items-center justify-between border-t border-slate-100 pt-1.5 mt-1.5">
                <span className="text-slate-500">Total Budget</span>
                <span className="font-bold text-slate-900 font-mono">{compactDollars(totalBudget)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
